import { Link } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { useRouterState } from "@tanstack/react-router";

type NavLeaf = { to: string; label: string; children?: NavLeaf[] };
type NavGroup = { label: string; children: NavLeaf[] };
type NavItem = NavLeaf | NavGroup;

const isGroup = (item: NavItem): item is NavGroup =>
  !("to" in item) && Array.isArray((item as NavGroup).children);

const nav: NavItem[] = [
  { to: "/about", label: "About" },
  {
    label: "Services",
    children: [
      { to: "/services", label: "Services Overview" },
      {
        to: "/seo",
        label: "SEO",
        children: [
          { to: "/seo/bespoke-seo-website-audits", label: "SEO Website Audits" },
          { to: "/seo/organic-seo-services", label: "Organic SEO Services" },
          { to: "/seo/link-building-outreach", label: "Link Building Outreach" },
          { to: "/seo/technical-seo-support", label: "Technical SEO Support" },
        ],
      },
      { to: "/web-design", label: "Web Design" },
      { to: "/wordpress-websites", label: "WordPress Websites" },
      { to: "/shopify-websites", label: "Shopify Websites" },
      { to: "/software-development", label: "Software Development" },
      { to: "/bespoke-app-development", label: "Bespoke App Development" },
      { to: "/website-management", label: "Website Management" },
      { to: "/hosting", label: "Hosting" },
    ],
  },
  {
    label: "Trades",
    children: [
      { to: "/seo/trades", label: "Trades Overview" },
      { to: "/seo/trades/builders", label: "Builders" },
      { to: "/seo/trades/electricians", label: "Electricians" },
      { to: "/seo/trades/plumbers", label: "Plumbers" },
      { to: "/seo/trades/contractors", label: "Contractors" },
    ],
  },
  {
    label: "Areas",
    children: [
      { to: "/areas", label: "Areas Overview" },
      { to: "/areas/barnsley", label: "Barnsley" },
      { to: "/seo/doncaster", label: "Doncaster" },
      { to: "/seo/huddersfield", label: "Huddersfield" },
      { to: "/seo/leeds", label: "Leeds" },
      { to: "/seo/rotherham", label: "Rotherham" },
      { to: "/seo/sheffield", label: "Sheffield" },
      { to: "/seo/wakefield", label: "Wakefield" },
    ],
  },
  { to: "/blog", label: "News" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState<string | null>(null);
  const [mobileNestedSub, setMobileNestedSub] = useState<string | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const [desktopSub, setDesktopSub] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const scrolledRef = useRef(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const mobileNavId = useId();
  const sidePanelTitleId = useId();
  const sidePanelRef = useRef<HTMLDivElement>(null);
  const sidePanelCloseRef = useRef<HTMLButtonElement>(null);
  const sidePanelTriggerRef = useRef<HTMLButtonElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    let frame = 0;

    const updateScrolled = () => {
      frame = 0;
      const shouldShrink = scrolledRef.current ? window.scrollY > 16 : window.scrollY > 72;
      if (shouldShrink !== scrolledRef.current) {
        scrolledRef.current = shouldShrink;
        setScrolled(shouldShrink);
      }
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(updateScrolled);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const isChildActive = (children: NavLeaf[] = []): boolean =>
    children.some((c) => {
      if (pathname === c.to) return true;
      if (c.children && c.children.length > 0) {
        // Parent-with-children only matches via its own descendants,
        // not by prefix (avoids /seo claiming /seo/trades/*).
        return isChildActive(c.children);
      }
      return pathname.startsWith(c.to + "/");
    });

  // Close desktop submenu on route change
  useEffect(() => {
    setDesktopSub(null);
  }, [pathname]);

  // Global Escape closes side panel
  useEffect(() => {
    if (!sideOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSideOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sideOpen]);

  // Side panel: focus management + focus trap + restore on close
  const wasSideOpenRef = useRef(false);
  useEffect(() => {
    if (!sideOpen) {
      // Only restore focus when the panel was actually opened/closed by the user,
      // not on initial mount (which would steal focus from the document).
      if (wasSideOpenRef.current) {
        sidePanelTriggerRef.current?.focus();
      }
      wasSideOpenRef.current = false;
      return;
    }
    wasSideOpenRef.current = true;
    // Move focus into the dialog
    const t = window.setTimeout(() => sidePanelCloseRef.current?.focus(), 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const root = sidePanelRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
  }, [sideOpen]);

  return (
    <header
      className={`site-header sticky top-0 z-40 border-b border-border bg-background/65 backdrop-blur-xl transition-all duration-300 ${scrolled ? "is-scrolled bg-background/95 shadow-lg shadow-background/70" : ""}`}
    >
      <div
        className={`mx-auto grid max-w-7xl grid-cols-[auto_1fr] items-center gap-5 px-6 transition-all duration-300 xl:grid-cols-[auto_1fr_auto_auto] xl:px-10 ${scrolled ? "py-3" : "py-6"}`}
      >
        <Link
          reloadDocument
          to="/"
          className="flex items-center gap-3"
          aria-label="Phil Carr — SEO Consultant & Web Strategist"
        >
          <img
            src="/images/phil-carr-logo.svg"
            alt="Phil Carr SEO consultant and web design specialist in Barnsley"
            data-name="Phil Carr SEO consultant and web design specialist in Barnsley"
            title="Phil Carr SEO consultant and web design specialist in Barnsley"
            width={177}
            height={51}
            className={`img-fluid w-auto shrink-0 transition-all duration-300 ${scrolled ? "h-9" : "h-12"}`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center justify-center gap-1 xl:flex">
          {nav.map((item) => {
            if (isGroup(item)) {
              return (
                <DesktopMenu
                  key={item.label}
                  group={item}
                  isOpen={desktopSub === item.label}
                  setOpen={(v) => setDesktopSub(v ? item.label : null)}
                  active={isChildActive(item.children)}
                />
              );
            }
            return (
              <Link
                reloadDocument
                key={item.to}
                to={item.to}
                className="rounded-[10px] px-4 py-3 text-sm font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                activeProps={{ className: "bg-secondary text-foreground font-semibold" }}
                activeOptions={{ exact: item.to === "/" }}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden xl:block">
          <Link
            reloadDocument
            to="/contact"
            className={`group inline-flex items-center gap-2 demo-button-primary demo-arrow-btn rounded-full text-sm font-bold transition-all duration-300 hover:-translate-y-0.5 ${scrolled ? "py-0 pl-5 pr-0" : "py-0 pl-7 pr-0"}`}
          >
            Book a consultation
            <span
              data-header-cta-icon
              className={`demo-cta-arrow-icon grid shrink-0 place-items-center rounded-full text-primary-foreground transition-transform group-hover:rotate-45 ${scrolled ? "h-10 w-10" : "h-12 w-12"}`}
            >
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setSideOpen(true)}
          aria-label="Open contact panel"
          aria-haspopup="dialog"
          aria-expanded={sideOpen}
          ref={sidePanelTriggerRef}
          className={`hidden place-items-center rounded-full border border-border bg-secondary/70 text-muted-foreground transition-all duration-300 hover:border-primary hover:text-primary focus-visible:outline-none xl:grid ${scrolled ? "h-10 w-10" : "h-12 w-12"}`}
        >
          <Menu className="h-4 w-4" />
        </button>

        <button
          ref={mobileToggleRef}
          type="button"
          className="grid h-12 w-12 place-items-center justify-self-end border border-border bg-secondary/70 text-muted-foreground transition-all duration-300 hover:border-primary hover:text-primary xl:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
          aria-expanded={open}
          aria-controls={mobileNavId}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <div id={mobileNavId} className="border-t border-border/60 xl:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            {nav.map((item) => {
              if (isGroup(item)) {
                const isOpen = mobileSub === item.label;
                const childActive = isChildActive(item.children);
                const subId = `mobile-sub-${item.label.replace(/\s+/g, "-").toLowerCase()}`;
                return (
                  <div key={item.label} className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setMobileSub(isOpen ? null : item.label)}
                      className={`flex items-center justify-between rounded-[10px] px-4 py-3 text-base font-semibold hover:bg-secondary hover:text-foreground ${
                        childActive ? "bg-secondary text-foreground" : "text-muted-foreground"
                      }`}
                      aria-expanded={isOpen}
                      aria-controls={subId}
                    >
                      {item.label}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <div id={subId} className="mt-1 flex flex-col">
                        {item.children.map((c) => {
                          const hasChildren = Boolean(c.children?.length);
                          const nestedOpen = mobileNestedSub === c.label;

                          return (
                            <div key={c.to} className="flex flex-col">
                              <div className="flex items-center">
                                <Link
                                  reloadDocument
                                  to={c.to}
                                  onClick={() => setOpen(false)}
                                  className="min-w-0 flex-1 rounded-[10px] px-6 py-2 text-base text-muted-foreground hover:bg-secondary hover:text-foreground"
                                  activeProps={{ className: "text-primary font-semibold" }}
                                  activeOptions={{ exact: true }}
                                >
                                  {c.label}
                                </Link>
                                {hasChildren && (
                                  <button
                                    type="button"
                                    onClick={() => setMobileNestedSub(nestedOpen ? null : c.label)}
                                    aria-expanded={nestedOpen}
                                    aria-label={`Toggle ${c.label} submenu`}
                                    className="mr-2 grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary"
                                  >
                                    <ChevronDown
                                      className={`h-4 w-4 transition-transform ${nestedOpen ? "rotate-180" : ""}`}
                                    />
                                  </button>
                                )}
                              </div>
                              {hasChildren && nestedOpen && (
                                <div className="mx-6 mb-2 mt-1 flex flex-col rounded-[10px] border border-border bg-card/95 py-2 shadow-lg shadow-black/30">
                                  {c.children?.map((sc) => (
                                    <Link
                                      reloadDocument
                                      key={sc.to}
                                      to={sc.to}
                                      onClick={() => setOpen(false)}
                                      className="px-4 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                                      activeProps={{ className: "text-primary font-semibold" }}
                                      activeOptions={{ exact: true }}
                                    >
                                      {sc.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  reloadDocument
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="rounded-[10px] px-4 py-3 text-base font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground"
                  activeProps={{ className: "bg-secondary text-foreground" }}
                  activeOptions={{ exact: item.to === "/" }}
                >
                  {item.label}
                </Link>
              );
            })}
            <Link
              reloadDocument
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center demo-button-primary rounded-full px-6 py-4 text-sm font-bold"
            >
              Book a consultation
            </Link>
          </nav>
        </div>
      )}

      {/* Off-canvas side info panel */}
      {mounted &&
        sideOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0 z-50 overflow-hidden opacity-100">
            <div
              className="absolute inset-0 bg-background/80 backdrop-blur-md"
              onClick={() => setSideOpen(false)}
            />
            <div
              ref={sidePanelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={sidePanelTitleId}
              className={`demo-side-panel absolute right-0 top-0 flex h-full w-full max-w-sm flex-col gap-8 overflow-y-auto border-l border-border bg-card p-8 shadow-[0_0_60px_rgba(0,0,0,0.6)] transition-transform duration-300 ${
                sideOpen ? "translate-x-0" : "translate-x-full"
              }`}
            >
              <div className="flex items-center justify-end">
                <button
                  ref={sidePanelCloseRef}
                  type="button"
                  onClick={() => setSideOpen(false)}
                  aria-label="Close panel"
                  className="grid h-10 w-10 place-items-center rounded-full border border-border text-muted-foreground hover:border-primary hover:text-primary"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div>
                <h2
                  id={sidePanelTitleId}
                  className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground"
                >
                  Have Questions?
                </h2>
                <ul className="mt-4 space-y-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                      <Phone className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Phone
                      </div>
                      <a href="tel:+441226697325" className="text-foreground hover:text-primary">
                        01226 697 325
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                      <Mail className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Email
                      </div>
                      <a
                        href="mailto:info@phil-carr.co.uk"
                        className="text-foreground hover:text-primary"
                      >
                        info@phil-carr.co.uk
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Location
                      </div>
                      <span className="text-foreground">Barnsley, South Yorkshire</span>
                    </div>
                  </li>
                </ul>
              </div>

              <Link
                reloadDocument
                to="/contact"
                onClick={() => setSideOpen(false)}
                className="group mt-auto inline-flex items-center justify-center gap-2 demo-button-primary rounded-full px-5 py-3 text-sm font-bold"
              >
                Book a consultation
                <span
                  data-header-cta-icon
                  className="demo-cta-arrow-icon grid h-8 w-8 shrink-0 place-items-center rounded-full text-primary-foreground transition-transform group-hover:rotate-45"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </span>
              </Link>
            </div>
          </div>,
          document.body,
        )}
    </header>
  );
}

function DesktopMenu({
  group,
  isOpen,
  setOpen,
  active,
}: {
  group: NavGroup;
  isOpen: boolean;
  setOpen: (v: boolean) => void;
  active: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [nestedOpen, setNestedOpen] = useState<string | null>(null);
  const menuId = useId();

  const focusItem = (index: number) => {
    const items = panelRef.current?.querySelectorAll<HTMLAnchorElement>("a[role='menuitem']");
    if (!items || items.length === 0) return;
    const i = (index + items.length) % items.length;
    items[i]?.focus();
  };

  // Close on outside click / focus-out
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onFocusIn = (e: FocusEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [isOpen, setOpen]);

  useEffect(() => {
    if (!isOpen) setNestedOpen(null);
  }, [isOpen]);

  const onButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => focusItem(0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => focusItem(-1));
    } else if (e.key === "Escape" && isOpen) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      panelRef.current?.querySelectorAll<HTMLAnchorElement>("a[role='menuitem']") ?? [],
    );
    const currentIndex = items.findIndex((el) => el === document.activeElement);

    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      focusItem(currentIndex + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focusItem(currentIndex - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusItem(-1);
    } else if (e.key === "Tab") {
      // Trap focus within the panel; Shift+Tab from first or Tab from last closes
      if (items.length === 0) return;
      if (e.shiftKey && currentIndex <= 0) {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      } else if (!e.shiftKey && currentIndex === items.length - 1) {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    } else if (e.key === "ArrowRight") {
      // Open nested submenu (if current item has one) and focus its first item
      const active = document.activeElement as HTMLElement | null;
      const itemRoot = active?.closest("[data-menu-item]") as HTMLElement | null;
      const label = itemRoot?.getAttribute("data-nested-label");
      if (label) {
        e.preventDefault();
        setNestedOpen(label);
        requestAnimationFrame(() => {
          const firstNested = itemRoot?.querySelector<HTMLAnchorElement>(
            "[data-nested-panel] a[role='menuitem']",
          );
          firstNested?.focus();
        });
      }
    } else if (e.key === "ArrowLeft") {
      // If focus is inside a nested panel, close it and return focus to parent menuitem
      const active = document.activeElement as HTMLElement | null;
      const nestedPanel = active?.closest("[data-nested-panel]") as HTMLElement | null;
      if (nestedPanel) {
        e.preventDefault();
        const itemRoot = nestedPanel.closest("[data-menu-item]") as HTMLElement | null;
        setNestedOpen(null);
        itemRoot?.querySelector<HTMLAnchorElement>("a[role='menuitem']")?.focus();
      }
    }
  };

  return (
    <div ref={containerRef} className="relative" onMouseEnter={() => setOpen(true)}>
      <button
        type="button"
        ref={buttonRef}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        onClick={() => setOpen(true)}
        onKeyDown={onButtonKeyDown}
        className={`inline-flex items-center gap-1.5 rounded-[10px] px-4 py-3 text-sm font-semibold transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:text-foreground ${
          active ? "bg-secondary text-foreground font-semibold" : "text-muted-foreground"
        }`}
      >
        {group.label}
        <ChevronDown
          className={`h-4 w-4 shrink-1 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`absolute left-0 top-full z-50 w-60 pt-3 transition-all duration-150 ${
          isOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <div
          id={menuId}
          ref={panelRef}
          role="menu"
          aria-label={group.label}
          onKeyDown={onPanelKeyDown}
          className="demo-dropdown rounded-[10px] border border-border bg-card/95 py-3 shadow-2xl shadow-black/50 backdrop-blur-md"
        >
          {group.children.map((c) => {
            const hasChildren = Boolean(c.children?.length);
            const childOpen = nestedOpen === c.label;
            const nestedId = `${menuId}-${c.label.replace(/\s+/g, "-").toLowerCase()}`;

            return (
              <div
                key={c.to}
                data-menu-item
                data-nested-label={hasChildren ? c.label : undefined}
                className="relative"
                onMouseEnter={() => hasChildren && setNestedOpen(c.label)}
                onMouseLeave={() => hasChildren && setNestedOpen(null)}
              >
                <div className="flex items-center">
                  <Link
                    reloadDocument
                    to={c.to}
                    role="menuitem"
                    aria-haspopup={hasChildren ? "menu" : undefined}
                    aria-expanded={hasChildren ? childOpen : undefined}
                    tabIndex={isOpen ? 0 : -1}
                    onClick={() => setOpen(false)}
                    onFocus={() => hasChildren && setNestedOpen(c.label)}
                    className="block min-w-0 flex-1 px-5 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:bg-secondary focus-visible:text-foreground focus-visible:outline-none"
                    activeProps={{ className: "text-primary font-semibold" }}
                    activeOptions={{ exact: true }}
                  >
                    {c.label}
                  </Link>
                  {hasChildren && (
                    <button
                      type="button"
                      aria-label={`${c.label} submenu`}
                      aria-haspopup="menu"
                      aria-expanded={childOpen}
                      aria-controls={nestedId}
                      tabIndex={isOpen ? 0 : -1}
                      onClick={() => setNestedOpen(childOpen ? null : c.label)}
                      onFocus={() => setNestedOpen(c.label)}
                      className="grid h-8 w-8 shrink-0 place-items-center text-muted-foreground transition-colors hover:bg-secondary hover:text-primary focus-visible:bg-secondary focus-visible:text-primary focus-visible:outline-none"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {hasChildren && (
                  <div
                    data-nested-panel
                    className={`absolute left-full top-0 z-50 w-64 pl-3 transition-all duration-150 ${
                      childOpen ? "visible opacity-100" : "invisible opacity-0"
                    }`}
                  >
                    <div
                      id={nestedId}
                      role="menu"
                      aria-label={`${c.label} submenu`}
                      className="demo-dropdown rounded-[10px] border border-border bg-card/95 py-3 shadow-2xl shadow-black/50 backdrop-blur-md"
                    >
                      {c.children?.map((sc) => (
                        <Link
                          reloadDocument
                          key={sc.to}
                          to={sc.to}
                          role="menuitem"
                          tabIndex={childOpen ? 0 : -1}
                          onClick={() => setOpen(false)}
                          className="block px-5 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:bg-secondary focus-visible:text-foreground focus-visible:outline-none"
                          activeProps={{ className: "text-primary font-semibold" }}
                          activeOptions={{ exact: true }}
                        >
                          {sc.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
